import { useMountEffect } from '@react-hookz/web/esm';
import React from 'react';
import { Box, Flex, Heading, Label } from '@apia/theme';
import ApiaApi, { makeApiaUrl } from '../../../../api/apiaApi/apiaApi';
import { useAppSelector, useAppDispatch } from '../../../../hooks/hooks';
import useModal from '../../../../hooks/useModal';
import {
  setActionFormSubmitState,
  updateActionEvent,
} from '../../../../store/appStateSlice';
import { hasSucceedValidation } from '../../../../store/previousValidationSlice';
import { arrayOrArray } from '../../../../utils/util';
import { Confirm, Modal } from '../../../common';
import { TModal } from '@apia/components';
import { releaseScreenLock } from '../../../common/dialogs/modals/ScreenLock';
import Checkbox from '../../../common/fields/Checkbox';
import Input from '../../../common/previousValidation/Input';
import useValidation from '../../../common/previousValidation/useValidation';
import ValidationContextProvider from '../../../common/previousValidation/ValidationContext';
import { markFormToSignURL } from './misc';
import propsStore from '../../../../store/propsStore/propsStore';
import { markFormToSign } from '../page/MarkToSignButton';

export interface IEncodedSignFile {
  f: string;
  i: string;
  l: string;
  p: string;
  t?: string;
  text?: string;
}

export interface IEncodedSignForm {
  i: string;
  m: string;
  p: string;
  r: string;
  t?: string;
  text?: string;
}

export interface IEncodedSignData {
  file?: IEncodedSignFile | IEncodedSignFile[];
  form?: IEncodedSignForm | IEncodedSignForm[];
  form_prev?: IEncodedSignForm | IEncodedSignForm[];
}

export type TSignFile = {
  field: string;
  label: string | undefined;
  section: string;
  formParent: string;
  id: string;
};

export type TSignForm = {
  formParent: string;
  label: string | undefined;
  id: string;
  required: string | undefined;
  checked: string;
};

function translateSignData(data: IEncodedSignData) {
  const files: TSignFile[] = arrayOrArray<IEncodedSignFile>(data.file).map(
    (file) => {
      return {
        field: file.l,
        formParent: file.p,
        id: file.i,
        label: file.t ?? file.text,
        section: file.f,
      };
    },
  );
  const forms: TSignForm[] = arrayOrArray<IEncodedSignForm>(data.form).map(
    (form) => ({
      checked: form.m,
      formParent: form.p,
      id: form.i,
      label: form.t ?? form.text,
      required: form.t,
    }),
  );
  const prevForms: TSignForm[] = arrayOrArray<IEncodedSignForm>(
    data.form_prev,
  ).map((form) => ({
    checked: form.m,
    formParent: form.p,
    id: form.i,
    label: form.t ?? form.text,
    required: form.t,
  }));

  return { files, forms, prevForms };
}

interface ISignFormModal {
  signData: IEncodedSignData;
}

const SignFormModal = ({
  signData,
  ...modalProps
}: ISignFormModal & TModal) => {
  const { files, forms, prevForms } = translateSignData(signData);

  const [confirmedForms, setConfirmedForms] = React.useState<TSignForm[]>(
    forms.filter((current) => current.checked),
  );

  const filesSections = files.reduce<Record<string, TSignFile[]>>(
    (sections, currentFile) => {
      const returnSections = { ...sections };
      if (!(currentFile.section in returnSections))
        returnSections[currentFile.section] = [];

      returnSections[currentFile.section].push(currentFile);

      return returnSections;
    },
    {},
  );

  React.useEffect(() => {}, [modalProps.isOpen]);

  const { show, onClose, ...props } = useModal();
  const dispatch = useAppDispatch();

  const closeModal = React.useCallback(
    (setAction?: boolean) => {
      onClose();
      if (modalProps?.onClose) modalProps.onClose();
      if (setAction) {
        releaseScreenLock();
        dispatch(setActionFormSubmitState(null));
      }
    },
    [dispatch, onClose, modalProps],
  );

  const actionState = useAppSelector(
    (global) => global.appStateSlice.prevActionState,
  );

  const validate = useValidation('signModal');

  useMountEffect(show);

  return (
    <ValidationContextProvider form="signModal">
      <Modal
        {...modalProps}
        noHeader
        onClose={() => closeModal(true)}
        {...props}
      >
        <Confirm
          onConfirm={async () => {
            if (actionState === 'btnNext') updateActionEvent('form-on-next');
            else {
              let signPin: string | undefined;
              if (window.DIGITAL_SIGN_NSS_ETOKEN && window.IS_LINUX) {
                const result = await validate();
                if (!hasSucceedValidation(result)) return;
                signPin = result.submitValues.signPin as string;
              }
              void ApiaApi.post(
                makeApiaUrl({
                  action: 'sign',
                }),
                {
                  handleLoad: true,
                  postData: signPin ? { pinFieldValue: signPin } : undefined,
                  postDataTreatement: 'stringify',
                },
              );
            }
            closeModal();
          }}
          onCancel={() => closeModal(true)}
        >
          <Box
            {...window.getVariant(
              'layout.execution.components.digitalSign.modal',
            )}
          >
            {window.DIGITAL_SIGN_NSS_ETOKEN &&
              window.IS_LINUX &&
              actionState !== 'btnNext' && (
                <Flex>
                  <Input
                    type="password"
                    name="signPin"
                    label={window.LBL_ENTER_PIN}
                    tooltip={window.LBL_ENTER_PIN}
                    validations={{ required: true }}
                  />
                </Flex>
              )}
            {forms.length > 0 && (
              <>
                <Heading as="h2">{window.TIT_SING_MODAL_LBL}</Heading>
                <ul>
                  {forms.map((form) => {
                    return (
                      <li key={`${form.formParent}_${form.id}`}>
                        <Label>
                          <Checkbox
                            disabled={
                              propsStore.getFieldProps(
                                `${form.formParent}_${form.id}`,
                              ).formData.requiredSignableForm
                            }
                            onChange={(ev) => {
                              void ApiaApi.post<{ success: boolean }>(
                                markFormToSignURL({
                                  frmId: form.id,
                                  frmParent: form.formParent,
                                  sign: ev.target.checked,
                                }),
                              ).then((res) => {
                                if (res?.data && res.data.success) {
                                  setConfirmedForms((currentConfirmed) => {
                                    markFormToSign(
                                      `${form.formParent}_${form.id}`,
                                      'markToSign',
                                      !ev.target.checked,
                                    );

                                    if (!ev.target.checked) {
                                      return [...currentConfirmed, form];
                                    }

                                    return currentConfirmed.filter(
                                      (confirmedForm) =>
                                        !(
                                          confirmedForm.id === form.id &&
                                          confirmedForm.formParent ===
                                            form.formParent
                                        ),
                                    );
                                  });
                                }
                              });
                            }}
                            checked={
                              !!confirmedForms.find(
                                (confirmedForm) =>
                                  confirmedForm.id === form.id &&
                                  confirmedForm.formParent === form.formParent,
                              )
                            }
                          />
                          {form.label}
                        </Label>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
            {prevForms.length > 0 && (
              <>
                <Heading as="h2">{window.TIT_SING_MODAL_PREV_LBL}</Heading>
                <ul>
                  {prevForms.map((form) => {
                    return (
                      <li key={`${form.formParent}_${form.id}`}>
                        <Label>
                          <Checkbox disabled checked />
                          {form.label}
                        </Label>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
            {files.length > 0 && (
              <>
                <Heading as="h2">{window.TIT_SING_DOCS_MODAL_LBL}</Heading>
                {Object.entries(filesSections).map(
                  ([sectionName, sectionFiles]) => (
                    <React.Fragment key={sectionName}>
                      <Heading as="h3">{sectionName}</Heading>
                      <ul>
                        {sectionFiles.map((file) => (
                          <li key={`${file.formParent}_${file.id}`}>
                            <Label>
                              <Checkbox checked disabled />
                              {file.field && `${file.field} - `}
                              {file.label}
                            </Label>
                          </li>
                        ))}
                      </ul>
                    </React.Fragment>
                  ),
                )}
              </>
            )}
          </Box>
        </Confirm>
      </Modal>
    </ValidationContextProvider>
  );
};

export default SignFormModal;
